import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import {
  Autocomplete,
  Avatar,
  Box,
  CircularProgress,
  Collapse,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';
import type { IBrand, IExternalBrand } from 'context/pages-context';
import type { IPageFormV2 } from 'modules/pages/pages/page-builder-v2/page-builder-v2.types';
import { type HTMLAttributes, type MouseEvent, FC, useEffect, useState } from 'react';
import { useController, useFormContext } from 'react-hook-form';
import { brandsApiSlice } from 'store/api/brands-api/brands-api';

import { styles } from './brand-select.styles';

interface BrandOptionUIProps {
  optionProps: HTMLAttributes<HTMLLIElement>;
  option: IBrand;
  selectedValue: number | undefined;
}

const BrandOptionUI: FC<BrandOptionUIProps> = ({ option, optionProps, selectedValue }) => {
  const { onClick, ...liProps } = optionProps;

  const isSelected = selectedValue === option.id;
  const [expanded, setExpanded] = useState(isSelected);

  const hasSubBrands = option.external_brands && option.external_brands.length > 0;

  const handleExpandClick = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    setExpanded(!expanded);
  };

  const handleSelect = (e: MouseEvent<HTMLDivElement>) => {
    if (onClick) {
      onClick(e as unknown as MouseEvent<HTMLLIElement>);
    }
  };

  return (
    <Box component="li" {...liProps} sx={styles.brandOptionWrapper}>
      <Box onClick={handleSelect} sx={styles.brandMainRow}>
        <Avatar
          src={option.img_logo || option.img_banner}
          sx={{ height: 28, width: 28 }}
          variant="rounded"
        />
        <Typography fontWeight="medium" sx={{ flexGrow: 1 }} variant="body2">
          {option.name}
        </Typography>

        {hasSubBrands && (
          <IconButton onClick={handleExpandClick} size="small" sx={{ ml: 'auto' }}>
            {expanded ? (
              <KeyboardArrowUpIcon fontSize="small" />
            ) : (
              <KeyboardArrowDownIcon fontSize="small" />
            )}
          </IconButton>
        )}
      </Box>

      {hasSubBrands && (
        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <Box sx={styles.subBrandList}>
            {option.external_brands.map((ext: IExternalBrand) => (
              <Box key={ext.system_id} onClick={handleSelect} sx={styles.subBrandItem}>
                <Typography color="text.secondary" variant="caption">
                  {ext.name}
                </Typography>
                {ext.brand_group === 'aos' && (
                  <Box
                    alt="aos"
                    component="img"
                    src="https://aos-uat.advoncommerce.com/favicon.ico"
                    sx={styles.aosIcon}
                  />
                )}
              </Box>
            ))}
          </Box>
        </Collapse>
      )}
    </Box>
  );
};

export const BrandSelect: FC<{ disabled?: boolean }> = ({ disabled }) => {
  const { control } = useFormContext<IPageFormV2>();
  const [inputValue, setInputValue] = useState<string>('');
  const [debouncedSearch, setDebouncedSearch] = useState<string>('');

  const { data, isFetching } = brandsApiSlice.useGetBrandsQuery({ search: debouncedSearch });
  const brands: IBrand[] = data?.brands || [];

  const {
    field,
    fieldState: { error },
  } = useController({ control, name: 'brand' });

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      setDebouncedSearch(inputValue);
    }, 300);

    return () => clearTimeout(delayDebounceFn);
  }, [inputValue]);

  const selectedBrand = field.value?.id
    ? brands.find((b) => b.id === field.value?.id) || field.value
    : null;

  return (
    <Autocomplete
      disabled={disabled}
      filterOptions={(options, state) => {
        const search = state.inputValue.toLowerCase();
        return options.filter(
          (opt) =>
            opt.name.toLowerCase().includes(search) ||
            opt.external_brands?.some((ext: IExternalBrand) =>
              ext.name.toLowerCase().includes(search),
            ),
        );
      }}
      getOptionLabel={(opt) => opt.name || ''}
      inputValue={inputValue}
      loading={isFetching}
      onChange={(_, val) => field.onChange(val || null)}
      onInputChange={(_, newInputValue) => setInputValue(newInputValue)}
      options={brands}
      renderInput={(params) => (
        <TextField
          {...params}
          error={!!error}
          helperText={error?.message}
          label="Brand (Optional)"
          size="small"
          slotProps={{
            input: {
              ...params.InputProps,
              endAdornment: (
                <>
                  {isFetching ? <CircularProgress color="inherit" size={20} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            },
          }}
        />
      )}
      renderOption={(props, option) => (
        <BrandOptionUI
          key={option.id}
          option={option}
          optionProps={props}
          selectedValue={field.value?.id}
        />
      )}
      slotProps={{
        listbox: { sx: { p: 0 } },
      }}
      value={selectedBrand}
    />
  );
};
